import React from 'react';
import { useTranslation } from 'react-i18next';
import { getDisplayStatusFromRecord, getStatusLabelKey } from '../../utils/statusConfig';
import { formatDisplayDateTime } from '../../utils/dateTimeFormat';
import { RtuCanonicalStatus } from '../../types/RtuStatus';

const ICON_BY_DISPLAY_STATUS: Record<string, string> = {
  online: 'pi pi-check',
  unstable: 'pi pi-exclamation-triangle',
  offline: 'pi pi-times',
  rebooting: 'pi pi-refresh',
  unknown: 'pi pi-minus',
};

export interface RtuDetailSummaryStripProps {
  /**
   * Canonical RTU status (Bug 3154: the only presentation source - never
   * derive status in the browser from file/ping timestamps). Legacy
   * `onlineStatus` boolean is intentionally not accepted here since it
   * cannot represent Unstable.
   */
  canonicalStatus?: RtuCanonicalStatus | null;
  systemRestart?: boolean | null;
  rtuId?: number | null;
  name?: string | null;
  snapshotTimestamp?: string | null;
  headerUtcTime?: string | null;
  actions?: React.ReactNode;
}

/**
 * Light-weight status row + last-updated chip + action icons (no page title).
 */
const RtuDetailSummaryStrip: React.FC<RtuDetailSummaryStripProps> = ({
  canonicalStatus,
  systemRestart,
  rtuId,
  name,
  snapshotTimestamp,
  headerUtcTime,
  actions,
}) => {
  const { t } = useTranslation();
  const tone = getDisplayStatusFromRecord({ canonicalStatus, systemRestart });
  // Bug 3154: the canonical visible state name is "Offline / Not Reachable"
  // (matching Dashboard and the Assets status filter), not the shorter
  // "Offline" label some other compact surfaces use for the same tone.
  const statusLabel = tone === 'offline'
    ? t('status.offlineNotReachable', { defaultValue: 'Offline / Not Reachable' })
    : t(getStatusLabelKey(tone));
  const titleName = name?.trim() || (rtuId != null ? t('rtuDetail.summary.deviceFallback', { value: rtuId }) : '—');
  const lastRaw = headerUtcTime?.trim() || snapshotTimestamp?.trim() || null;
  const lastDisplay = formatDisplayDateTime(lastRaw);
  const statusAria = t('rtuDetail.summary.statusAria', { status: statusLabel, name: titleName });

  const iconClass = ICON_BY_DISPLAY_STATUS[tone] || ICON_BY_DISPLAY_STATUS.unknown;

  return (
    <div className="rtu-detail-summary">
      <div
        className={`rtu-detail-summary__status rtu-detail-summary__status--${tone}`}
        role="status"
        aria-label={statusAria}
      >
        <span className="rtu-detail-summary__status-icon-badge" aria-hidden>
          <i className={iconClass} />
        </span>
        <span className="rtu-detail-summary__status-text">
          <span className="rtu-detail-summary__status-label">{statusLabel}</span>
          <span className="rtu-detail-summary__status-sep"> — </span>
          <span className="rtu-detail-summary__status-name">{titleName}</span>
        </span>
      </div>

      <div className="rtu-detail-summary__updated">
        <span className="rtu-detail-summary__updated-prefix">{t('rtuDetail.summary.lastCollected')}</span>
        <strong className="rtu-detail-summary__updated-value">{lastDisplay}</strong>
      </div>

      {actions ? <div className="rtu-detail-summary__actions">{actions}</div> : null}
    </div>
  );
};

export default RtuDetailSummaryStrip;
